import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

function StatusPill({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    none: "bg-slate-50 text-slate-700 border-slate-200",
    confirmed: "bg-blue-50 text-blue-700 border-blue-200",
    shipped: "bg-violet-50 text-violet-700 border-violet-200",
    delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest font-mono border ${map[status] || map.none}`}>
      {status}
    </span>
  );
}

function DoctorOverview({ user }) {
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    api.get("/orders/me").then((r) => setOrders(r.data));
    // `api` is a module-level axios instance; setOrders is stable. Fetch once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const idStatus = user.id_document_status || "none";
  const licenseStatus = user.license_status || "none";
  const fullyApproved = idStatus === "approved" && licenseStatus === "approved";
  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Verification banner — most important info on mobile */}
      {!fullyApproved && (
        <Link
          to="/dashboard/verification"
          data-testid="dashboard-verification-banner"
          className="block border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5 p-4 sm:p-5 active:bg-[hsl(var(--accent))]/10 transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5">
              <span className="inline-block w-2 h-2 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-sm sm:text-base">
                {idStatus === "approved" || licenseStatus === "approved"
                  ? "One step left to unlock ordering"
                  : "Verification required to unlock ordering"}
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                <span>ID: <StatusPill status={idStatus} /></span>
                <span>License: <StatusPill status={licenseStatus} /></span>
              </div>
            </div>
            <span className="shrink-0 font-mono text-xs text-[hsl(var(--accent))] self-center">→</span>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        <div className="border border-border bg-white p-3 sm:p-5 col-span-2 lg:col-span-1">
          <div className="overline">Doctor</div>
          <div className="font-display text-base sm:text-lg mt-1 sm:mt-2 truncate">{user.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user.specialty || "Specialty: —"}</div>
          {user.doctor_license && <div className="text-xs text-muted-foreground truncate">License: {user.doctor_license}</div>}
        </div>
        <div className="border border-border bg-white p-3 sm:p-5">
          <div className="overline">Identity</div>
          <div className="mt-2 sm:mt-3"><StatusPill status={idStatus} /></div>
          <Link to="/dashboard/verification" className="text-xs underline mt-2 sm:mt-3 inline-block">Manage →</Link>
        </div>
        <div className="border border-border bg-white p-3 sm:p-5">
          <div className="overline">License</div>
          <div className="mt-2 sm:mt-3"><StatusPill status={licenseStatus} /></div>
          <Link to="/dashboard/verification" className="text-xs underline mt-2 sm:mt-3 inline-block">Manage →</Link>
        </div>
        <div className="border border-border bg-white p-3 sm:p-5 col-span-2 lg:col-span-1">
          <div className="overline">Orders</div>
          <div className="font-display text-2xl mt-1 sm:mt-2">{orders.length}</div>
          <div className="text-xs text-muted-foreground">{fullyApproved ? "ordering enabled" : "verification pending"}</div>
        </div>
      </div>

      <div>
        <div className="overline mb-3">Recent orders</div>
        {orders.length === 0 ? (
          <div className="border border-border bg-white p-6 sm:p-8 text-center text-sm text-muted-foreground">
            {fullyApproved
              ? <>No orders yet. <Link to="/catalog" className="underline">Browse catalog</Link>.</>
              : <>You will be able to order once both your ID and your medical license are approved.</>}
          </div>
        ) : (
          <div className="border border-border bg-white">
            {orders.map((o, idx) => (
              <div key={o.order_id} className={`p-4 sm:p-5 flex flex-wrap gap-3 justify-between items-center ${idx > 0 ? "border-t border-border" : ""}`}>
                <div className="min-w-0">
                  <div className="font-mono text-xs text-muted-foreground">{o.order_id}</div>
                  <div className="font-display font-semibold mt-1 text-sm sm:text-base">${o.total.toFixed(2)} · {o.items.length} item(s)</div>
                  <div className="text-xs text-muted-foreground mt-1">{new Date(o.created_at).toLocaleString()}</div>
                </div>
                <StatusPill status={o.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user } = useAuth();

  // Stable object for the Outlet context — DoctorOverview & StatusPill are
  // module-level references; only `user` changes meaningfully.
  const outletContext = useMemo(
    () => ({ user, DoctorOverview, StatusPill }),
    [user]
  );

  if (!user || user === false) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
      <div className="overline mb-2">Dashboard</div>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tighter">
        {user.role === "admin" ? "Administrator console"
          : `Welcome, Dr. ${user.name?.split(" ").pop() || ""}`}
      </h1>

      {user.role === "admin" && (
        <nav className="mt-8 flex gap-4 border-b border-border overflow-x-auto no-scrollbar" data-testid="admin-tabs">
          {[
            { to: "/dashboard", label: "Overview", end: true },
            { to: "/dashboard/verifications", label: "Verifications" },
            { to: "/dashboard/products", label: "Products" },
            { to: "/dashboard/categories", label: "Categories" },
            { to: "/dashboard/orders", label: "Orders" },
            { to: "/dashboard/users", label: "Users" },
            { to: "/dashboard/settings", label: "Settings" },
          ].map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`admin-tab-${t.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `pb-3 -mb-px text-sm border-b-2 whitespace-nowrap shrink-0 ${
                  isActive ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
      )}

      <div className="mt-10">
        <Outlet context={outletContext} />
      </div>
    </div>
  );
}

export { StatusPill, DoctorOverview };
